import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/hooks/useTheme'
import { notificationsApi } from '@/lib/api'
import { Notification } from '@/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  LayoutDashboard,
  Receipt,
  CreditCard,
  Building2,
  FileText,
  CalendarDays,
  Settings,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  ArrowLeft,
  Bell,
  AlertCircle,
  AlertTriangle,
  Info,
  Check,
  HelpCircle,
  Home,
  UserPlus,
  Target,
  TrendingDown,
  SlidersHorizontal,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { ReportIssueDialog } from './ReportIssueDialog'

const navItems = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/bills', label: 'Bills', icon: Receipt },
  { href: '/transactions', label: 'Transactions', icon: CreditCard },
  { href: '/patterns', label: 'Patterns', icon: SlidersHorizontal },
  { href: '/debt', label: 'Debt', icon: TrendingDown },
  { href: '/accounts', label: 'Accounts', icon: Building2 },
  { href: '/reports', label: 'Reports', icon: FileText },
  { href: '/goals', label: 'Goals', icon: Target },
  { href: '/calendar', label: 'Calendar', icon: CalendarDays },
  { href: '/settings', label: 'Settings', icon: Settings },
  { href: '/about', label: 'About', icon: HelpCircle },
]

// Map notification types to navigation paths
const notificationNavMap: Record<string, string> = {
  bill_due_today: '/bills',
  email_connection_error: '/settings?tab=email',
  simplefin_sync_error: '/accounts',
  parsed_email_ready: '/settings?tab=email',
  pending_user_registration: '/settings?tab=users',
  household_invitation: '/settings?tab=household',
  household_member_joined: '/settings?tab=household',
  invitation_accepted: '/settings?tab=household',
}

// Get icon based on severity
function getSeverityIcon(severity: string) {
  switch (severity) {
    case 'error':
      return <AlertCircle className="h-4 w-4 text-destructive" />
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />
    default:
      return <Info className="h-4 w-4 text-blue-500" />
  }
}

// Get severity color class
function getSeverityClass(severity: string) {
  switch (severity) {
    case 'error':
      return 'border-l-destructive'
    case 'warning':
      return 'border-l-yellow-500'
    default:
      return 'border-l-blue-500'
  }
}

function NotificationBell() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)

  // Fetch notifications
  const { data: notifications = [] } = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: async () => {
      const res = await notificationsApi.getAll()
      return res.data
    },
    refetchInterval: 10000, // 10 seconds
    refetchIntervalInBackground: true,
  })

  // Mark notification as read
  const markRead = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  // Mark all as read
  const markAllRead = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const unreadCount = notifications.filter(n => n.read === 0).length

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (notification.read === 0) {
      markRead.mutate(notification.id)
    }

    // Navigate to appropriate page
    const path = notificationNavMap[notification.type]
    if (path) {
      navigate(path)
    }

    setIsOpen(false)
  }

  const handleMarkAllRead = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    markAllRead.mutate()
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          title={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1 text-xs"
              onClick={handleMarkAllRead}
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No notifications
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={cn(
                  'flex flex-col items-start gap-1 p-3 cursor-pointer border-l-2',
                  getSeverityClass(notification.severity),
                  notification.read === 0 ? 'bg-accent/50' : ''
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-center gap-2 w-full">
                  {getSeverityIcon(notification.severity)}
                  <span className={cn(
                    'text-sm flex-1 truncate',
                    notification.read === 0 ? 'font-medium' : ''
                  )}>
                    {notification.title}
                  </span>
                </div>
                {notification.message && (
                  <p className="text-xs text-muted-foreground line-clamp-2 pl-6">
                    {notification.message}
                  </p>
                )}
                <span className="text-xs text-muted-foreground pl-6">
                  {new Date(notification.created_at).toLocaleString()}
                </span>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { theme, setTheme, resolvedTheme } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showHouseholdPrompt, setShowHouseholdPrompt] = useState(false)
  const [installPrompt, setInstallPrompt] = useState<any>(null)

  useEffect(() => {
    const handler = (e: Event) => { e.preventDefault(); setInstallPrompt(e) }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const handleInstall = async () => {
    if (!installPrompt) return
    installPrompt.prompt()
    const result = await installPrompt.userChoice
    if (result.outcome === 'accepted') setInstallPrompt(null)
  }

  const isHome = location.pathname === '/'

  // Check if user needs household setup (any approved user with no household)
  const needsHouseholdSetup = user && !user.household_id

  // Show household prompt when user needs setup
  useEffect(() => {
    if (needsHouseholdSetup) {
      setShowHouseholdPrompt(true)
    }
  }, [needsHouseholdSetup])

  const cycleTheme = () => {
    if (theme === 'light') setTheme('dark')
    else if (theme === 'dark') setTheme('system')
    else setTheme('light')
  }

  const ThemeIcon = theme === 'system' ? Monitor : resolvedTheme === 'dark' ? Moon : Sun

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden flex items-center gap-2 p-4 border-b">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        {!isHome && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="flex items-center gap-2 flex-1">
          <Receipt className="h-6 w-6 text-primary" />
          <span className="font-semibold">Centsry</span>
        </div>
        <NotificationBell />
        <Button
          variant="ghost"
          size="icon"
          onClick={cycleTheme}
          title={`Theme: ${theme}`}
        >
          <ThemeIcon className="h-5 w-5" />
        </Button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r transform transition-transform lg:translate-x-0 lg:static',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="hidden lg:flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-2">
                <Receipt className="h-8 w-8 text-primary" />
                <span className="text-xl font-bold">Centsry</span>
              </div>
              <NotificationBell />
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                )
              })}
            </nav>

            {/* User info and theme toggle */}
            <div className="p-4 border-t space-y-3">
              <ReportIssueDialog />
              {installPrompt && (
                <Button variant="outline" size="sm" onClick={handleInstall} className="w-full mt-2">
                  Install App
                </Button>
              )}
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={cycleTheme}
                  title={`Theme: ${theme}`}
                >
                  <ThemeIcon className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 p-4 lg:p-8 min-h-screen">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Household Setup Dialog */}
      <Dialog open={showHouseholdPrompt} onOpenChange={setShowHouseholdPrompt}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="h-5 w-5" />
              Welcome to Centsry!
            </DialogTitle>
            <DialogDescription>
              To keep your financial data private and organized, please create or join a household.
              This allows you to share bills and expenses with family or roommates.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            <Button
              onClick={() => {
                setShowHouseholdPrompt(false)
                navigate('/settings?tab=household&action=create')
              }}
              className="w-full"
            >
              <Home className="h-4 w-4 mr-2" />
              Create a Household
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowHouseholdPrompt(false)
                navigate('/settings?tab=household&action=join')
              }}
              className="w-full"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Join an Existing Household
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowHouseholdPrompt(false)}
              className="w-full text-muted-foreground"
            >
              Skip for now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
